'use client';
import { createContext, useContext, useState, ReactNode, Dispatch, SetStateAction, useCallback } from 'react';

type PdfItem = {
  id: string;
  file: File;
};

interface PdfToolContextType {
  items: PdfItem[];
  setItems: Dispatch<SetStateAction<PdfItem[]>>;
  jobId: string | null;
  setJobId: (id: string | null) => void;
  resultFilename: string | null;
  setResultFilename: (name: string | null) => void;
  resultStatus: string | null;
  setResultStatus: (status: string | null) => void;
  originalSize: number | null;
  setOriginalSize: (size: number | null) => void;
  compressedSize: number | null;
  setCompressedSize: (size: number | null) => void;
  toolParams: any;
  setToolParams: (params: any) => void;
  resetContext: () => void;
}

const PdfToolContext = createContext<PdfToolContextType | undefined>(undefined);

export function PdfToolProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<PdfItem[]>([]);
  const [jobId, setJobId] = useState<string | null>(null);
  const [resultFilename, setResultFilename] = useState<string | null>(null);
  const [resultStatus, setResultStatus] = useState<string | null>(null);
  const [originalSize, setOriginalSize] = useState<number | null>(null);
  const [compressedSize, setCompressedSize] = useState<number | null>(null);
  const [toolParams, setToolParams] = useState<any>(null);

  const resetContext = useCallback(() => {
    setItems([]);
    setJobId(null);
    setResultFilename(null);
    setResultStatus(null);
    setOriginalSize(null);
    setCompressedSize(null);
    setToolParams(null);
  }, []);

  return (
    <PdfToolContext.Provider value={{ 
      items, setItems, 
      jobId, setJobId, 
      resultFilename, setResultFilename,
      resultStatus, setResultStatus,
      originalSize, setOriginalSize,
      compressedSize, setCompressedSize,
      toolParams, setToolParams,
      resetContext 
    }}>
      {children}
    </PdfToolContext.Provider>
  );
}

export function usePdfTool() {
  const context = useContext(PdfToolContext);
  if (context === undefined) {
    throw new Error('usePdfTool must be used within a PdfToolProvider');
  }
  return context;
}
